// jsx_runtime.js — a tiny string-rendering hyperscript so we can port the
// Magic Sheep design prototype's JSX (React + Babel) into this Hotwire app
// WITHOUT pulling React into the codebase (house style is Stimulus).
//
// esbuild is configured (package.json `build` script) with
//   --jsx-factory=h --jsx-fragment=Fragment
//   --inject:./app/javascript/lib/jsx_runtime.js
// so every `<div/>` compiles to `h("div", props, ...children)` and `h`/`Fragment`
// are auto-imported from here.
//
// The art is essentially static SVG, so h() returns an HTML *string* (wrapped in
// a Raw marker) rather than DOM nodes. That keeps inline <svg> namespacing correct
// (the browser parses it when set via innerHTML) and lets Stimulus controllers
// re-render sections by swapping innerHTML. Interactivity (minimap drag, scene
// picker, envelope open, upload) lives in Stimulus controllers, not inline handlers,
// so function-valued props (onClick, …) are intentionally dropped here.

// Raw marks an already-rendered HTML fragment so it is NOT re-escaped when nested.
export class Raw {
  constructor(html) { this.html = html }
  toString() { return this.html }
}

const VOID = new Set([
  "area", "base", "br", "col", "embed", "hr", "img", "input",
  "link", "meta", "param", "source", "track", "wbr",
  // SVG self-closing
  "path", "circle", "ellipse", "rect", "line", "polyline", "polygon",
  "stop", "use", "feGaussianBlur", "feColorMatrix", "feTurbulence",
  "feOffset", "feBlend", "feComposite", "feMerge", "feMergeNode", "feFlood",
]);

// React camelCase prop -> real markup attribute name. Only the hyphenated SVG/HTML
// attributes need mapping; genuine SVG camelCase attrs (viewBox, stdDeviation,
// baseFrequency, numOctaves, preserveAspectRatio, patternUnits, patternTransform)
// pass through verbatim, as do aria-*/data-*.
const ATTR = {
  className: "class",
  htmlFor: "for",
  stopColor: "stop-color",
  stopOpacity: "stop-opacity",
  strokeWidth: "stroke-width",
  strokeDasharray: "stroke-dasharray",
  strokeDashoffset: "stroke-dashoffset",
  strokeLinecap: "stroke-linecap",
  strokeLinejoin: "stroke-linejoin",
  strokeOpacity: "stroke-opacity",
  strokeMiterlimit: "stroke-miterlimit",
  fillOpacity: "fill-opacity",
  fillRule: "fill-rule",
  clipRule: "clip-rule",
  clipPath: "clip-path",
  floodColor: "flood-color",
  floodOpacity: "flood-opacity",
  textAnchor: "text-anchor",
  dominantBaseline: "dominant-baseline",
  letterSpacing: "letter-spacing",
  colorInterpolationFilters: "color-interpolation-filters",
};

// CSS properties that take a unitless number (everything else gets `px` appended,
// matching React's behaviour the prototype relies on).
const UNITLESS = new Set([
  "opacity", "zIndex", "fontWeight", "lineHeight", "flex", "flexGrow",
  "flexShrink", "order", "zoom", "aspectRatio", "gridRow", "gridColumn",
  "fillOpacity", "strokeOpacity", "strokeDashoffset", "columnCount",
]);

function escapeText(s) {
  return String(s).replace(/[&<>]/g, (c) => (c === "&" ? "&amp;" : c === "<" ? "&lt;" : "&gt;"));
}
function escapeAttr(s) {
  return String(s).replace(/[&"]/g, (c) => (c === "&" ? "&amp;" : "&quot;"));
}

function kebab(prop) {
  return prop.startsWith("--") ? prop : prop.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
}

function styleString(obj) {
  if (typeof obj === "string") return obj;
  const parts = [];
  for (const key in obj) {
    let v = obj[key];
    if (v == null || v === false) continue;
    if (typeof v === "number" && !UNITLESS.has(key)) v = v + "px";
    parts.push(`${kebab(key)}:${v}`);
  }
  return parts.join(";");
}

function attrsString(props) {
  let out = "";
  for (const key in props) {
    if (key === "children" || key === "key" || key === "ref") continue;
    let v = props[key];
    if (v == null || v === false) continue;
    if (typeof v === "function") continue; // event handlers handled by Stimulus
    if (key === "style") { out += ` style="${escapeAttr(styleString(v))}"`; continue; }
    if (key === "dangerouslySetInnerHTML") continue;
    const name = ATTR[key] || key;
    if (v === true) { out += ` ${name}`; continue; }
    out += ` ${name}="${escapeAttr(v)}"`;
  }
  return out;
}

function renderChildren(children) {
  let out = "";
  const walk = (c) => {
    if (c == null || c === false || c === true) return;
    if (Array.isArray(c)) { c.forEach(walk); return; }
    if (c instanceof Raw) { out += c.html; return; }
    out += escapeText(c);
  };
  walk(children);
  return out;
}

export function Fragment(props) {
  return new Raw(renderChildren(props && props.children));
}

export function h(type, props, ...children) {
  props = props || {};
  if (children.length) props = { ...props, children };

  if (typeof type === "function") {
    const result = type(props);
    return result instanceof Raw ? result : new Raw(renderChildren(result));
  }

  const inner = props.dangerouslySetInnerHTML
    ? props.dangerouslySetInnerHTML.__html
    : renderChildren(props.children);

  if (!inner && VOID.has(type)) return new Raw(`<${type}${attrsString(props)} />`);
  return new Raw(`<${type}${attrsString(props)}>${inner}</${type}>`);
}

// Mount a rendered tree (Raw or component result) into a DOM element.
export function mount(el, node) {
  el.innerHTML = node instanceof Raw ? node.html : renderChildren(node);
}
